/**
 * ExpedienteTabs — navegacion entre las cuatro zonas de trabajo (paso 3).
 *
 * Cada pestana es un `<h2>` navegable: reemplaza al scroll de nueve
 * secciones apiladas por cuatro destinos con nombre. El usuario siempre
 * sabe en cual esta porque la pestana activa lo dice.
 *
 * Accesibilidad: patron ARIA de tabs completo -- `role="tablist"`, flechas
 * para moverse, `aria-selected`, y el panel apuntando a su tab con
 * `aria-labelledby`. Un `<select>` disfrazado de pestanas es la forma
 * habitual de romper esto; aca los tabs son botones de verdad.
 */

import { useRef, type ReactNode } from "react";

import type { ExpedienteTab } from "./focus";

export interface TabDef {
  id: ExpedienteTab;
  label: string;
  /** Contador opcional al lado del label (ej. "5/9"). */
  badge?: string;
}

export function ExpedienteTabs({
  tabs,
  active,
  onChange,
  children,
}: {
  tabs: TabDef[];
  active: ExpedienteTab;
  onChange: (tab: ExpedienteTab) => void;
  children: ReactNode;
}) {
  const listRef = useRef<HTMLDivElement>(null);

  /** Flechas para moverse entre pestanas, como manda el patron ARIA. */
  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const delta = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
    if (delta === 0) return;
    e.preventDefault();
    const idx = tabs.findIndex((t) => t.id === active);
    const next = tabs[(idx + delta + tabs.length) % tabs.length];
    if (!next) return;
    onChange(next.id);
    // El foco sigue a la seleccion: si no, el lector de pantalla anuncia
    // una pestana y el teclado sigue parado en otra.
    listRef.current?.querySelector<HTMLButtonElement>(`#tab-${next.id}`)?.focus();
  }

  return (
    <div>
      <div
        ref={listRef}
        role="tablist"
        aria-label="Secciones del expediente"
        onKeyDown={handleKeyDown}
        data-testid="expediente-tablist"
        className="flex flex-wrap gap-1 border-b border-slate-200"
      >
        {tabs.map((tab) => {
          const isActive = tab.id === active;
          return (
            <button
              key={tab.id}
              id={`tab-${tab.id}`}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`panel-${tab.id}`}
              tabIndex={isActive ? 0 : -1}
              data-testid={`tab-${tab.id}`}
              onClick={() => onChange(tab.id)}
              className={`-mb-px min-h-[44px] rounded-t-lg border-b-2 px-4 py-2 text-sm font-semibold transition-colors focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 focus:outline-hidden ${
                isActive
                  ? "border-brand-600 text-brand-700"
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              {tab.label}
              {tab.badge && (
                <span
                  className={`ml-2 text-xs tabular-nums ${
                    isActive ? "text-brand-600" : "text-slate-400"
                  }`}
                >
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div
        id={`panel-${active}`}
        role="tabpanel"
        aria-labelledby={`tab-${active}`}
        tabIndex={0}
        data-testid={`panel-${active}`}
        className="pt-5 focus:outline-hidden"
      >
        {children}
      </div>
    </div>
  );
}
